// The index of a Parliament record in the side panel, presentational: the
// title, the switches that widen or narrow the tree (routine entries, on at
// the opening; speeches; notes), and the entries as a list indented by level; an entry
// with a position is a button («vai al punto»), one without is plain text
// with a tooltip. Every element is one the design already has:
//   title        PanelTitle, sidebar/panels.tsx:31-45
//   switches     the «Vista» buttons of the sources panel as they were at
//                284af26 (src/components/sidebar/panels.tsx:153-171: aria-pressed, circleCheck/circle)
//   list         ScrollArea, panels.tsx:48-53; the rows of «Fonti eliminate», panels.tsx:231-238
//                (pl-7 per level, no strike-through)
//   entry button the accent text link (text-sm font-medium text-accent), the look the ways back had until 20/10/2026
//   empty state  PanelStatus
// The data comes ready (sectionTree): nothing here calls the api.
import { Button } from "@heroui/react";

import type { IndexEntry, IndexFilter } from "../../features/parliament/adapt";
import { Icon } from "../Icon";
import { PanelStatus } from "../PanelStatus";
import { PanelTitle, ScrollArea } from "../sidebar/panels";

export const INDEX_TITLE = "Indice";
export const NO_POSITION = "Posizione non disponibile: apri l’originale";
export const NO_ENTRIES = "Nessuna voce con questi interruttori.";

const SWITCHES: { key: keyof IndexFilter; label: string }[] = [
  { key: "routine", label: "Mostra voci di routine" },
  { key: "speeches", label: "Mostra interventi" },
  { key: "notes", label: "Mostra note" },
];

/** One level of the tree is the indent of a row of «Fonti eliminate» (pl-7: 1.75rem). */
const LEVEL_INDENT_REM = 1.75;

type SectionIndexProps = {
  entries: IndexEntry[];
  filter: IndexFilter;
  onFilterChange: (next: IndexFilter) => void;
  /** The entry «vai al punto» last went to (aria-current); null before the first. */
  current: number | null;
  onGo: (ordinal: number) => void;
  /** Closes the panel: the chevron of the title (NewsLayout). */
  onCollapse: () => void;
};

export function SectionIndex({ entries, filter, onFilterChange, current, onGo, onCollapse }: SectionIndexProps) {
  return (
    <nav aria-label={INDEX_TITLE} className="flex flex-col gap-[30px]">
      <PanelTitle title={INDEX_TITLE} onCollapse={onCollapse} />
      <div role="group" aria-label="Mostra" className="flex flex-wrap items-center gap-2.5">
        {SWITCHES.map(({ key, label }) => {
          const active = filter[key];
          return (
            <Button
              key={key}
              size="sm"
              onPress={() => onFilterChange({ ...filter, [key]: !active })}
              aria-pressed={active}
              className={`h-7 min-w-8 gap-1 rounded-2xl px-2.5 py-1 text-xs leading-5 font-medium ${
                active ? "bg-accent text-default" : "bg-white text-field-placeholder shadow-field"
              }`}
            >
              <Icon name={active ? "circleCheck" : "circle"} />
              {label}
            </Button>
          );
        })}
      </div>
      {entries.length === 0 ? (
        <PanelStatus indent={false}>{NO_ENTRIES}</PanelStatus>
      ) : (
        <ScrollArea maxHeight={496}>
          <ul className="flex flex-col">
            {entries.map((entry) => (
              <li
                key={entry.ordinal}
                className="py-2 text-sm leading-6 text-muted"
                style={{ paddingLeft: `${entry.depth * LEVEL_INDENT_REM}rem` }}
              >
                {entry.reachable ? (
                  <button
                    type="button"
                    onClick={() => onGo(entry.ordinal)}
                    aria-current={current === entry.ordinal ? "true" : undefined}
                    className="cursor-pointer rounded-sm text-left text-sm leading-6 font-medium text-accent"
                  >
                    {entry.label}
                  </button>
                ) : (
                  <span title={entry.positionMissing ? NO_POSITION : undefined}>{entry.label}</span>
                )}
              </li>
            ))}
          </ul>
        </ScrollArea>
      )}
    </nav>
  );
}
